import React from 'react'

import { UpsellProvider, useUpsell } from 'modules/upselling_kit/lib/upsellContext'
import { UpsellingKitModule } from 'modules/upselling_kit/types'

type BaseProps = {
  children?: React.ReactNode
  loadingComponent?: React.ReactNode
  fallbackComponent?: React.ReactNode
}
type Props = UpsellingKitModule & BaseProps

const GuardUpsellComponent = ({ children, loadingComponent, fallbackComponent }: BaseProps) => {
  const {
    isAllowed,
    loading,
    isAlreadyInstalled,
    isFeatureAlreadyAvailable,
    moduleAvailable,
    featureRequested,
  } = useUpsell()

  if (loading) {
    return loadingComponent
  }

  // If featureRequested is provided, check feature availability (don't require moduleAvailable)
  // Otherwise, require moduleAvailable and check module installation
  if (featureRequested) {
    const shouldShowUpselling = isAllowed && !isFeatureAlreadyAvailable
    return shouldShowUpselling ? children : fallbackComponent
  }

  // For module-based upsells, require moduleAvailable
  if (!moduleAvailable) {
    return fallbackComponent
  }

  const shouldShowUpselling = isAllowed && !isAlreadyInstalled
  return shouldShowUpselling ? children : fallbackComponent
}

export const UpsellGuard = ({
  children,
  loadingComponent,
  moduleRequested,
  requestSource,
  requestReason,
  featureRequested,
  fallbackComponent,
}: Props) => {
  return (
    <UpsellProvider
      moduleRequested={moduleRequested}
      requestSource={requestSource}
      requestReason={requestReason}
      featureRequested={featureRequested}
    >
      <GuardUpsellComponent
        loadingComponent={loadingComponent}
        fallbackComponent={fallbackComponent}
      >
        {children}
      </GuardUpsellComponent>
    </UpsellProvider>
  )
}
